import React, { useState, useEffect } from 'react';
import './App.css';
import StudyTimer from './components/StudyTimer';
import SessionHistory from './components/SessionHistory';
import ProgressTracker from './components/ProgressTracker';
import TaskManager from './components/TaskManager';
import PomodoroTimer from './components/PomodoroTimer';
import Achievements from './components/Achievements';
import FocusMode from './components/FocusMode';
import Goals from './components/Goals';
import DataManager from './components/DataManager';

function App() {
  const [sessions, setSessions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('timer');
  const [selectedTask, setSelectedTask] = useState(null);
  const [focusModeActive, setFocusModeActive] = useState(false);

  useEffect(() => {
    const savedSessions = localStorage.getItem('studySessions');
    if (savedSessions) {
      setSessions(JSON.parse(savedSessions));
    }

    const savedTasks = localStorage.getItem('studyTasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  const addSession = (session) => {
    const updatedSessions = [...sessions, session];
    setSessions(updatedSessions);
    localStorage.setItem('studySessions', JSON.stringify(updatedSessions));
  };

  const handleDataImport = (data) => {
    setSessions(data.sessions || []);
    setTasks(data.tasks || []);
    localStorage.setItem('studySessions', JSON.stringify(data.sessions || []));
    localStorage.setItem('studyTasks', JSON.stringify(data.tasks || []));
  };

  const updateTasks = (updatedTasks) => {
    setTasks(updatedTasks);
    localStorage.setItem('studyTasks', JSON.stringify(updatedTasks));
  };

  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    if (activeTab !== 'timer' && activeTab !== 'pomodoro') {
      setActiveTab('timer');
    }
  };

  const toggleFocusMode = () => {
    setFocusModeActive(!focusModeActive);
  };

  return (
    <div className={`App ${focusModeActive ? 'focus-mode-active' : ''}`}>
      <header className="App-header">
        <div className="header-content">
          <h1>🎓 Study Timer Pro</h1>
          <div className="header-controls">
            <button
              onClick={toggleFocusMode}
              className={`focus-mode-btn ${focusModeActive ? 'active' : ''}`}
            >
              {focusModeActive ? '🔴 Exit Focus' : '🎯 Focus Mode'}
            </button>
          </div>
        </div>
        <nav className="nav-tabs">
          <button
            className={activeTab === 'timer' ? 'active' : ''}
            onClick={() => setActiveTab('timer')}
          >
            ⏱️ Timer
          </button>
          <button
            className={activeTab === 'pomodoro' ? 'active' : ''}
            onClick={() => setActiveTab('pomodoro')}
          >
            🍅 Pomodoro
          </button>
          <button
            className={activeTab === 'tasks' ? 'active' : ''}
            onClick={() => setActiveTab('tasks')}
          >
            📝 Tasks
          </button>
          <button
            className={activeTab === 'goals' ? 'active' : ''}
            onClick={() => setActiveTab('goals')}
          >
            🎯 Goals
          </button>
          <button
            className={activeTab === 'history' ? 'active' : ''}
            onClick={() => setActiveTab('history')}
          >
            📊 History
          </button>
          <button
            className={activeTab === 'progress' ? 'active' : ''}
            onClick={() => setActiveTab('progress')}
          >
            📈 Progress
          </button>
          <button
            className={activeTab === 'achievements' ? 'active' : ''}
            onClick={() => setActiveTab('achievements')}
          >
            🏆 Achievements
          </button>
          <button
            className={activeTab === 'focus' ? 'active' : ''}
            onClick={() => setActiveTab('focus')}
          >
            🎯 Focus
          </button>
          <button
            className={activeTab === 'data' ? 'active' : ''}
            onClick={() => setActiveTab('data')}
          >
            💾 Data
          </button>
        </nav>
      </header>

      <main className="App-main">
        {activeTab === 'timer' && (
          <StudyTimer
            onSessionComplete={addSession}
            selectedTask={selectedTask}
          />
        )}
        {activeTab === 'pomodoro' && (
          <PomodoroTimer
            onSessionComplete={addSession}
            selectedTask={selectedTask}
          />
        )}
        {activeTab === 'tasks' && (
          <TaskManager
            onTaskSelect={handleTaskSelect}
            selectedTask={selectedTask}
          />
        )}
        {activeTab === 'goals' && <Goals sessions={sessions} tasks={tasks} />}
        {activeTab === 'history' && <SessionHistory sessions={sessions} />}
        {activeTab === 'progress' && <ProgressTracker sessions={sessions} />}
        {activeTab === 'achievements' && (
          <Achievements sessions={sessions} tasks={tasks} />
        )}
        {activeTab === 'focus' && (
          <FocusMode
            isActive={focusModeActive}
            onToggle={toggleFocusMode}
          />
        )}
        {activeTab === 'data' && (
          <DataManager
            sessions={sessions}
            tasks={tasks}
            onDataImport={handleDataImport}
          />
        )}
      </main>

      {selectedTask && (
        <div className="selected-task-banner">
          <div className="banner-content">
            <span>
              📝 Working on: <strong>{selectedTask.title}</strong>
            </span>
            <button
              onClick={() => setSelectedTask(null)}
              className="clear-task-btn"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
